// Разбор качества для технолога, который в цех не заходит.
//
// У него нет ни пресса под рукой, ни плиты, которую можно потрогать. Всё, что
// у него есть, — эти цифры. И если экран покажет ему двадцать таблиц, он будет
// искать в них глазами то, что машина считает за секунду.
//
// Поэтому экран открывается НЕ таблицей, а выводами: короткими фразами о том,
// на что стоит потратить время, и кнопкой под каждой — «показать листы».
// Таблица никуда не делась, она третьей вкладкой; но начинается разбор с
// ответа, а не с данных, из которых ответ ещё надо добыть.
//
// Три вкладки — три вопроса, и они идут в том порядке, в каком их задают:
//
//     Выводы   куда смотреть              программа говорит сама
//     Срезы    где именно хуже            человек выбирает признак
//     Реестр   из чего это сложилось      до конкретной бирки
//
// Фильтр ОДИН на все три. Что бы человек ни выбрал, все вкладки считаются по
// одной выборке: иначе в выводах «47 листов», в таблице 51, и оба числа
// честные — просто посчитаны по-разному. Расходящимся цифрам не верят обеим.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { qualityAnalyticsAPI as api } from '../api';

// Разделы — кнопками над содержимым. Раньше они жили в боковом меню, и
// технолог их не находил: открывал экран, видел фразы и не понимал, где листы.
const TABS = [
  { key: 'insights', label: '🔎 Где проблема' },
  { key: 'slices',   label: '▦ Сравнить' },
  { key: 'sheets',   label: '▤ Все листы' },
];

// ── Общие мелочи оформления ─────────────────────────────────────────────────
const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: 16,
};
const TH = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: 'var(--dim)', textTransform: 'uppercase', whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)', position: 'sticky', top: 0,
  background: 'var(--card)', zIndex: 1,
};
const TD = {
  padding: '8px 10px', fontSize: 13, color: 'var(--text)',
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const BTN = {
  padding: '7px 13px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--input)', color: 'var(--text)', fontSize: 13,
  cursor: 'pointer',
};
const BTN_MAIN = { ...BTN, background: 'var(--accent)', color: '#fff',
  border: '1px solid var(--accent)', fontWeight: 600 };

// Число с пробелом между тысячами: «4 312». Слипшееся «4312» в отчёте о браке
// читается медленнее, а числа здесь сравнивают глазами.
const n = (v) => (v === null || v === undefined ? '—'
  : String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
// Знак у сдвига несёт смысл: «холоднее» и «горячее» — разные поломки.
const signed = (v, unit) => (v === null || v === undefined ? '—'
  : `${v > 0 ? '+' : ''}${v}${unit ? ' ' + unit : ''}`);

// ── Фильтр: что именно выбрано ──────────────────────────────────────────────
//
// Списочные признаки держим массивами, числовые — парой границ. Один и тот же
// объект уходит во все четыре ручки, поэтому собирается он ровно один раз, в
// `toParams`: два места сборки означали бы два разных запроса под одним
// фильтром.
const EMPTY = {
  days: 30, from: '', to: '',
  // Цех: пусто — все разрешённые разом, сложенными цифрами.
  branch: '',
  press: [], operator: [], product: [], texture: [], grade: [], reason: [],
  shift: [], dsp_batch: [], film_pallet: [], thickness: [],
  only: '', source: '', area: '', code: '',
  measured: false, drop_stale: false, stale: false,
  // Реестр: состояние самого листа. Три значения, а не два — «не важно» не то
  // же самое, что «нет»: подставить сюда «нет» значило бы спрятать всё
  // отгруженное, ни разу об этом не сказав.
  transferred: '', shipped: '', regraded: '', no_label: '', voided: '',
  order: '', accepted_by: [],
  ranges: {},
};
const LIST_KEYS = ['press', 'operator', 'product', 'texture', 'grade',
  'reason', 'shift', 'dsp_batch', 'film_pallet', 'thickness', 'accepted_by'];

function toParams(f) {
  const p = {};
  if (f.from || f.to) {
    if (f.from) p.from = f.from;
    if (f.to) p.to = f.to;
  } else {
    p.days = f.days;
  }
  // Цех кладём в запрос и сами, хотя перехватчик подставляет его тоже:
  // выгрузка идёт fetch'ем мимо перехватчика, и без этого файл вышел бы по
  // всему заводу при экране по одному цеху.
  if (f.branch) p.branch = f.branch;
  LIST_KEYS.forEach(k => { if (f[k] && f[k].length) p[k] = f[k].join(','); });
  ['only', 'source', 'area', 'code', 'transferred', 'shipped', 'regraded',
    'no_label', 'voided', 'order'].forEach(k => { if (f[k]) p[k] = f[k]; });
  if (f.measured) p.measured = 1;
  if (f.drop_stale) p.drop_stale = 1;
  if (f.stale) p.stale = 1;
  Object.entries(f.ranges || {}).forEach(([k, v]) => {
    if (v && v[0] !== '' && v[0] !== undefined && v[0] !== null) p[`${k}_min`] = v[0];
    if (v && v[1] !== '' && v[1] !== undefined && v[1] !== null) p[`${k}_max`] = v[1];
  });
  return p;
}

// Условия из вывода — в состояние фильтра.
//
// Приходят они с сервера уже посчитанными («press: 2», «temp_bottom_min: 185»),
// и переводить их обратно на экране нельзя иначе как один в один: любая
// вольность здесь означает, что кнопка «показать листы» открыла не те листы, о
// которых был вывод, — а это хуже, чем отсутствие кнопки.
function applyInsightFilter(f, add) {
  const next = { ...f, ranges: { ...f.ranges } };
  Object.entries(add || {}).forEach(([k, v]) => {
    // Числовое условие приходит парой `<величина>_min` / `_max`. Списочные
    // признаки на такие имена не заканчиваются, поэтому разбор однозначен.
    const m = k.match(/^(.+)_(min|max)$/);
    if (m && !LIST_KEYS.includes(m[1])) {
      const cur = next.ranges[m[1]] || ['', ''];
      next.ranges[m[1]] = m[2] === 'min' ? [v, cur[1]] : [cur[0], v];
      return;
    }
    if (LIST_KEYS.includes(k)) { next[k] = [v]; return; }
    if (k === 'branch') { next.branch = String(v); return; }
    if (k === 'stale') { next.stale = true; return; }
    next[k] = v;
  });
  return next;
}

// ── Мелкие детали экрана ────────────────────────────────────────────────────

function Chip({ children, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px',
      background: 'var(--accent-weak)', border: '1px solid var(--accent)',
      borderRadius: 20, fontSize: 12, color: 'var(--text)',
    }}>
      {children}
      {onRemove && (
        <button onClick={onRemove} style={{
          border: 'none', background: 'none', color: 'var(--muted)',
          cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0,
        }}>×</button>
      )}
    </span>
  );
}

// Полоска доли. Нужна не для красоты: «1.2% и 4.8%» глазом различаются хуже,
// чем две полоски разной длины, а сравнивают здесь именно доли.
function Bar({ value, max, danger }) {
  const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 6, background: 'var(--input)', borderRadius: 3,
      overflow: 'hidden', minWidth: 60 }}>
      <div style={{ width: `${w}%`, height: '100%',
        background: danger ? 'var(--danger)' : 'var(--accent)' }} />
    </div>
  );
}

function Muted({ children, size = 12 }) {
  return <span style={{ color: 'var(--dim)', fontSize: size }}>{children}</span>;
}

// Выбор из списка с галочками. Обычный <select multiple> в браузере требует
// держать Ctrl — в цехе и на ноутбуке это одинаково неудобно и вдобавок
// незаметно: человек выбирает одно значение и теряет предыдущее.
function Picker({ title, options, value, onChange, idKey = 'id', nameKey = 'name' }) {
  const [open, setOpen] = useState(false);
  const sel = value || [];
  if (!options || !options.length) return null;
  const toggle = (id) => {
    const s = String(id);
    onChange(sel.includes(s) ? sel.filter(x => x !== s) : [...sel, s]);
  };
  return (
    <div style={{ minWidth: 190 }}>
      <button onClick={() => setOpen(!open)} style={{ ...BTN, width: '100%',
        textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}>
        <span>{title}{sel.length ? ` · ${sel.length}` : ''}</span>
        <span style={{ color: 'var(--dim)' }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 4, maxHeight: 220, overflowY: 'auto',
          border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--card)' }}>
          {options.map(o => (
            <label key={o[idKey]} style={{ display: 'flex', gap: 8,
              alignItems: 'center', padding: '6px 10px', fontSize: 13,
              cursor: 'pointer' }}>
              <input type="checkbox" checked={sel.includes(String(o[idKey]))}
                onChange={() => toggle(o[idKey])} />
              <span style={{ flex: 1 }}>{o[nameKey]}</span>
              {o.sheets !== undefined && <Muted>{n(o.sheets)}</Muted>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Панель фильтра ──────────────────────────────────────────────────────────
//
// Открывается по кнопке и закрывается сразу после выбора. Держать сорок полей
// раскрытыми постоянно — значит отдать им пол-экрана и заставить человека
// проматывать мимо них к цифрам, ради которых он и пришёл.
function FilterPanel({ facets, f, set, onClose }) {
  const [showMode, setShowMode] = useState(
    Object.keys(f.ranges || {}).length > 0);
  const upd = (k, v) => set({ ...f, [k]: v });
  const range = (key, i, v) => {
    const cur = f.ranges[key] || ['', ''];
    const nx = i === 0 ? [v, cur[1]] : [cur[0], v];
    const ranges = { ...f.ranges };
    if (nx[0] === '' && nx[1] === '') delete ranges[key]; else ranges[key] = nx;
    set({ ...f, ranges });
  };

  return (
    <div style={{ ...CARD, marginBottom: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <Picker title="Пресс" options={facets.press} value={f.press}
          onChange={v => upd('press', v)} />
        <Picker title="Оператор" options={facets.operator} value={f.operator}
          onChange={v => upd('operator', v)} />
        <Picker title="Смена" options={facets.shift} value={f.shift}
          onChange={v => upd('shift', v)} />
        <Picker title="Продукт" options={facets.product} value={f.product}
          onChange={v => upd('product', v)} />
        <Picker title="Текстура" options={facets.texture} value={f.texture}
          onChange={v => upd('texture', v)} />
        <Picker title="Сорт" options={facets.grade} value={f.grade}
          onChange={v => upd('grade', v)} />
        <Picker title="Причина" options={facets.reason} value={f.reason}
          onChange={v => upd('reason', v)} />
        <Picker title="Партия ДСП" value={f.dsp_batch}
          options={(facets.dsp_batch || []).map(x => ({ id: x, name: x }))}
          onChange={v => upd('dsp_batch', v)} />
        <Picker title="Паллета плёнки" value={f.film_pallet}
          options={(facets.film_pallet || []).map(x => ({ id: x, name: x }))}
          onChange={v => upd('film_pallet', v)} />
        <Picker title="Кто принял" options={facets.accepted_by}
          value={f.accepted_by} onChange={v => upd('accepted_by', v)} />
      </div>

      {/* Состояние листа — вопросы реестра, а не разбора брака: где лист
          сейчас, трогали ли его после приёмки, считается ли он вообще. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12,
        alignItems: 'center' }}>
        {[
          ['transferred', 'На складе'],
          ['shipped', 'Отгружен'],
          ['regraded', 'Пересорт'],
          ['no_label', 'Бирку не печатали'],
        ].map(([key, title]) => (
          <label key={key} style={{ fontSize: 13, display: 'flex', gap: 6,
            alignItems: 'center' }}>
            {title}
            <select value={f[key]} onChange={e => upd(key, e.target.value)}
              style={{ ...BTN, padding: '6px 8px' }}>
              <option value="">не важно</option>
              <option value="1">да</option>
              <option value="0">нет</option>
            </select>
          </label>
        ))}
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          Не в счёте
          <select value={f.voided} onChange={e => upd('voided', e.target.value)}
            style={{ ...BTN, padding: '6px 8px' }}>
            {/* Выведенный из счёта лист не идёт ни в остаток, ни в отчёты —
                но бирка у него живая, и реестр умеет её показать. */}
            <option value="">без них</option>
            <option value="with">вместе с ними</option>
            <option value="only">только они</option>
          </select>
        </label>
        <input placeholder="номер заказа" value={f.order}
          onChange={e => upd('order', e.target.value)}
          style={{ ...BTN, width: 150 }} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14,
        marginTop: 12, alignItems: 'center' }}>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          Показывать
          <select value={f.only} onChange={e => upd('only', e.target.value)}
            style={{ ...BTN, padding: '6px 8px' }}>
            <option value="">все листы</option>
            <option value="defects">только не 1-й сорт</option>
            <option value="good">только 1-й сорт</option>
          </select>
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          <input type="checkbox" checked={f.measured}
            onChange={e => upd('measured', e.target.checked)} />
          только замеренные станком
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          <input type="checkbox" checked={f.drop_stale}
            onChange={e => upd('drop_stale', e.target.checked)} />
          убрать просроченные показания
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          <input type="checkbox" checked={f.stale}
            onChange={e => upd('stale', e.target.checked)} />
          только просроченные
        </label>
        <input placeholder="код листа" value={f.code}
          onChange={e => upd('code', e.target.value)}
          style={{ ...BTN, width: 130, textTransform: 'uppercase' }} />
      </div>

      <button onClick={() => setShowMode(!showMode)} style={{
        ...BTN, marginTop: 12, background: 'none', border: 'none',
        padding: 0, color: 'var(--accent)' }}>
        {showMode ? '▴' : '▾'} Режим прессования — границы
      </button>
      {showMode && (
        <div style={{ display: 'grid', marginTop: 10, gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {(facets.numeric || []).map(p => (
            <div key={p.key} style={{ display: 'flex', gap: 6,
              alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: 'var(--muted)',
                flex: 1 }}>{p.title}{p.unit ? `, ${p.unit}` : ''}</span>
              <input type="number" placeholder="от" style={{ ...BTN, width: 70 }}
                value={(f.ranges[p.key] || ['', ''])[0]}
                onChange={e => range(p.key, 0, e.target.value)} />
              <input type="number" placeholder="до" style={{ ...BTN, width: 70 }}
                value={(f.ranges[p.key] || ['', ''])[1]}
                onChange={e => range(p.key, 1, e.target.value)} />
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button style={BTN_MAIN} onClick={onClose}>Применить</button>
        <button style={BTN} onClick={() => set({ ...EMPTY, days: f.days, branch: f.branch })}>
          Сбросить всё
        </button>
      </div>
    </div>
  );
}

// ── Строка активного фильтра ────────────────────────────────────────────────
//
// Выбранное должно быть ВИДНО, а не спрятано за закрытой панелью. Иначе через
// пять минут человек смотрит на 47 листов, забыв, что час назад сузил выборку
// одним прессом, и делает вывод про завод по одному станку.
function ActiveFilters({ f, facets, set }) {
  const chips = [];
  const nameOf = (kind, id) => {
    const list = facets[kind] || [];
    const found = list.find(o => String(o.id) === String(id));
    return found ? found.name : id;
  };
  const TITLES = {
    press: 'Пресс', operator: 'Оператор', product: 'Продукт',
    texture: 'Текстура', grade: 'Сорт', reason: 'Причина', shift: 'Смена',
    dsp_batch: 'Партия', film_pallet: 'Паллета', thickness: 'Толщина',
    accepted_by: 'Принял',
  };
  LIST_KEYS.forEach(k => (f[k] || []).forEach(v => chips.push({
    label: `${TITLES[k]}: ${['dsp_batch', 'film_pallet', 'thickness'].includes(k) ? v : nameOf(k, v)}`,
    drop: () => set({ ...f, [k]: f[k].filter(x => x !== v) }),
  })));
  if (f.only) chips.push({
    label: f.only === 'defects' ? 'Только не 1-й сорт' : 'Только 1-й сорт',
    drop: () => set({ ...f, only: '' }) });
  if (f.measured) chips.push({ label: 'Только замеры станка',
    drop: () => set({ ...f, measured: false }) });
  if (f.drop_stale) chips.push({ label: 'Без просроченных',
    drop: () => set({ ...f, drop_stale: false }) });
  if (f.stale) chips.push({ label: 'Только просроченные',
    drop: () => set({ ...f, stale: false }) });
  if (f.source) chips.push({
    label: f.source === 'device' ? 'Режим со станка' : 'Режим со слов смены',
    drop: () => set({ ...f, source: '' }) });
  if (f.code) chips.push({ label: `Код: ${f.code}`,
    drop: () => set({ ...f, code: '' }) });
  if (f.order) chips.push({ label: `Заказ: ${f.order}`,
    drop: () => set({ ...f, order: '' }) });
  // Состояние листа: выбранное обязано быть видно. Иначе человек через пять
  // минут смотрит на пустую таблицу, забыв, что оставил только отгруженные.
  [['transferred', 'На складе'], ['shipped', 'Отгружен'],
    ['regraded', 'Пересорт'], ['no_label', 'Бирку не печатали'],
  ].forEach(([k, title]) => {
    if (!f[k]) return;
    chips.push({ label: `${title}: ${f[k] === '1' ? 'да' : 'нет'}`,
      drop: () => set({ ...f, [k]: '' }) });
  });
  if (f.voided) chips.push({
    label: f.voided === 'only' ? 'Только не в счёте' : 'Вместе с не в счёте',
    drop: () => set({ ...f, voided: '' }) });
  Object.entries(f.ranges || {}).forEach(([k, v]) => {
    const t = (facets.numeric || []).find(p => p.key === k);
    chips.push({
      label: `${t ? t.title : k}: ${v[0] !== '' ? v[0] : '…'}–${v[1] !== '' ? v[1] : '…'}`,
      drop: () => { const r = { ...f.ranges }; delete r[k]; set({ ...f, ranges: r }); },
    });
  });
  if (f.from || f.to) chips.push({
    label: `Период: ${f.from || '…'} — ${f.to || '…'}`,
    drop: () => set({ ...f, from: '', to: '' }) });

  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
      {chips.map((c, i) => (
        <Chip key={i} onRemove={c.drop}>{c.label}</Chip>
      ))}
      <button onClick={() => set({ ...EMPTY, days: f.days, branch: f.branch })} style={{
        ...BTN, padding: '4px 10px', fontSize: 12 }}>очистить</button>
    </div>
  );
}

// ── Вкладка «Где проблема» ──────────────────────────────────────────────────
//
// Технолог приходит сюда с одним вопросом: ГДЕ. Какой цех, какой пресс, какой
// оператор, какая смена — и на каком режиме они работают. Поэтому экран
// начинается не с фраз, а с четырёх таблиц рядом: худшее сверху, красным, и
// тут же режим этой группы. Любая строка открывает свои листы сбоку, не
// уводя с экрана: сравнение остаётся перед глазами.

// Режим группы одной строкой. Пусто — так и говорим: «не записан» не то же
// самое, что «в норме».
function modeLine(m) {
  if (!m) return '';
  const parts = [];
  if (m.temp_top !== null && m.temp_top !== undefined) parts.push(`верх ${m.temp_top}°`);
  if (m.temp_bottom !== null && m.temp_bottom !== undefined) parts.push(`низ ${m.temp_bottom}°`);
  if (m.hold !== null && m.hold !== undefined) parts.push(`${m.hold} с`);
  if (m.pressure !== null && m.pressure !== undefined) parts.push(`давл. ${m.pressure}`);
  return parts.length ? parts.join(' · ') : 'режим не записан';
}

// Во сколько раз хуже — это и есть «проблема». Полтора раза и больше при
// заметном числе брака — красным; остальное спокойным цветом, чтобы красное
// было видно, а не терялось среди красного.
const isBad = (r) => r.ratio !== null && r.ratio >= 1.5 && r.defects >= 3;

function ProblemCard({ group, onShow, limit = 6 }) {
  const [all, setAll] = useState(false);
  if (!group || !group.rows || !group.rows.length) return null;
  const rows = all ? group.rows : group.rows.slice(0, limit);
  const maxRate = Math.max(...group.rows.map(r => r.rate), 0.01);
  return (
    <div style={{ ...CARD, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6 }}>
        <b style={{ fontSize: 14 }}>{group.title}</b>
        <span style={{ marginLeft: 'auto' }}>
          <Muted>в среднем {pct(group.overall)}</Muted>
        </span>
      </div>
      {rows.map((r, i) => {
        const can = r.filter && Object.keys(r.filter).length > 0;
        const bad = isBad(r);
        return (
          <div key={i}
            onClick={() => can && onShow(r.filter, `${group.title}: ${r.name}`)}
            title={can ? 'Показать листы' : ''}
            style={{ padding: '7px 6px', borderRadius: 6,
              cursor: can ? 'pointer' : 'default',
              borderTop: i ? '1px solid var(--border)' : 'none',
              background: bad ? 'rgba(229,97,94,.08)' : 'transparent' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ flex: 1, fontSize: 13.5, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontWeight: bad ? 700 : 400 }}>{r.name}</span>
              <div style={{ width: 70 }}><Bar value={r.rate} max={maxRate} danger={bad} /></div>
              <b style={{ width: 52, textAlign: 'right', fontSize: 13.5,
                color: bad ? 'var(--danger)' : 'var(--text)' }}>{pct(r.rate)}</b>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <span style={{ flex: 1 }}><Muted size={11.5}>{modeLine(r.mode)}</Muted></span>
              <Muted size={11.5}>{n(r.defects)} из {n(r.sheets)}</Muted>
            </div>
          </div>
        );
      })}
      {group.rows.length > limit && (
        <button onClick={() => setAll(!all)} style={{ ...BTN, marginTop: 6,
          padding: '4px 10px', fontSize: 12 }}>
          {all ? 'свернуть' : `все ${group.rows.length}`}
        </button>
      )}
      {group.hidden > 0 && (
        <div style={{ marginTop: 4 }}>
          <Muted size={11}>ещё {group.hidden} — меньше 10 листов, не сравниваем</Muted>
        </div>
      )}
    </div>
  );
}

function InsightsTab({ data, onShow }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) return <Muted size={14}>Считаю…</Muted>;

  const ov = data.overview || {};
  // Цех в обзоре только когда их больше одного: одна строка «Алматы — 100%
  // листов» ничего не сравнивает.
  const cards = ['branch', 'press', 'operator', 'shift']
    .map(k => ov[k])
    .filter(g => g && g.rows && g.rows.length > (g.dim === 'branch' ? 1 : 0));

  const t = data.trend;
  const real = (data.findings || []).filter(x => !x.explained_by);
  const top = expanded ? real : real.slice(0, 5);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...CARD, fontSize: 15, lineHeight: 1.5 }}>
        Принято <b>{n(data.sheets ?? (data.summary || {}).sheets)}</b> листов, не 1-го сорта{' '}
        <b>{n(data.defects ?? (data.summary || {}).defects)}</b> —{' '}
        {pct(data.base_rate ?? (data.summary || {}).rate)}.
        {t && (
          <> Стало <b style={{ color: t.direction === 'хуже'
            ? 'var(--danger)' : 'var(--success)' }}>{t.direction}</b>:{' '}
            {pct(t.new_rate)} во второй половине периода против{' '}
            {pct(t.old_rate)} в первой
            {t.significant ? '' : ' (разница может быть случайной)'}.</>
        )}
        <div style={{ marginTop: 4 }}>
          <Muted size={12.5}>Нажмите на любую строку — откроются её листы.</Muted>
        </div>
      </div>

      {cards.length > 0 && (
        <div style={{ display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {cards.map(g => <ProblemCard key={g.dim} group={g} onShow={onShow} />)}
        </div>
      )}

      {(data.mode || []).length > 0 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 8,
            textTransform: 'uppercase' }}>Режим у брака не такой, как у годных</h3>
          {data.mode.map((m, i) => (
            <div key={i} style={{ padding: '5px 0', fontSize: 14 }}>
              <b>{m.title}</b>: у брака {m.bad_avg}, у годных {m.good_avg} —{' '}
              <b style={{ color: 'var(--danger)' }}>{Math.abs(m.diff)} {m.unit} {m.direction}</b>{' '}
              <Muted>· {m.confidence}</Muted>
            </div>
          ))}
        </div>
      )}

      {!data.enough ? (
        <div style={{ ...CARD, color: 'var(--muted)', fontSize: 13.5 }}>
          Листов в выборке мало — система не делает выводов меньше чем по{' '}
          {data.min_sheets} листам. Цифры выше честные, но сравнивать осторожно.
        </div>
      ) : real.length > 0 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 8,
            textTransform: 'uppercase' }}>Система заметила</h3>
          {top.map((x, i) => {
            const can = x.filter && Object.keys(x.filter).length > 0;
            return (
              <div key={i} onClick={() => can && onShow(x.filter, `${x.title}: ${x.value}`)}
                style={{ padding: '8px 4px', fontSize: 14, lineHeight: 1.4,
                  cursor: can ? 'pointer' : 'default',
                  borderTop: i ? '1px solid var(--border)' : 'none' }}>
                <b>{x.title}: «{x.value}»</b> — брака{' '}
                <b style={{ color: 'var(--danger)' }}>{pct(x.rate)}</b> против{' '}
                {pct(x.rest_rate)} у остальных.{' '}
                <Muted>{n(x.sheets)} листов · {x.confidence}</Muted>
                {can && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>листы →</span>}
              </div>
            );
          })}
          {real.length > top.length && (
            <button style={{ ...BTN, marginTop: 6 }} onClick={() => setExpanded(true)}>
              Ещё {real.length - top.length}
            </button>
          )}
        </div>
      )}

      {(data.reasons || []).length > 0 && (
        <div style={CARD}>
          <h3 style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 8,
            textTransform: 'uppercase' }}>Из-за чего брак</h3>
          {data.reasons.map((r, i) => (
            <div key={i} onClick={() => onShow(r.filter, `Причина: ${r.name}`)}
              style={{ display: 'flex', gap: 10, alignItems: 'center',
                padding: '5px 4px', fontSize: 14, cursor: 'pointer' }}>
              <span style={{ width: 200 }}>{r.name}</span>
              <div style={{ flex: 1 }}><Bar value={r.share} max={100} danger /></div>
              <span style={{ width: 90, textAlign: 'right' }}>
                {n(r.count)} <Muted>· {pct(r.share)}</Muted>
              </span>
            </div>
          ))}
        </div>
      )}

      {(data.health || []).length > 0 && (
        <div style={{ ...CARD, borderLeft: '3px solid var(--warning)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning)',
            marginBottom: 6 }}>ЧЕМУ ЗДЕСЬ НЕЛЬЗЯ ВЕРИТЬ</div>
          {data.health.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center',
              padding: '4px 0', fontSize: 13 }}>
              <span style={{ flex: 1 }}>{h.text}</span>
              {h.filter && Object.keys(h.filter).length > 0 && (
                <button style={{ ...BTN, padding: '3px 9px', fontSize: 12 }}
                  onClick={() => onShow(h.filter, h.text)}>листы</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Листы сбоку ─────────────────────────────────────────────────────────────
//
// Нажали строку — листы открываются поверх, справа, а экран под ними остаётся
// тем же. Раньше кнопка уводила на другую вкладку, и чтобы посмотреть второй
// пресс, приходилось возвращаться и заново находить, где был.
function SheetsDrawer({ title, facets, params, onExport, onOpenFull, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.35)' }}>
      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute',
        top: 0, right: 0, bottom: 0, width: 'min(1150px, 96vw)',
        background: 'var(--bg, var(--card))', overflowY: 'auto', padding: 16,
        boxShadow: '-8px 0 28px rgba(0,0,0,.35)' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center',
          flexWrap: 'wrap', marginBottom: 12 }}>
          <b style={{ fontSize: 16, flex: 1 }}>Листы — {title}</b>
          <button style={BTN} onClick={onOpenFull}>Оставить этот фильтр</button>
          <button style={BTN_MAIN} onClick={onClose}>✕ Закрыть</button>
        </div>
        <RegistryTab facets={facets} params={params} onExport={onExport} />
      </div>
    </div>
  );
}

// ── Вкладка «Срезы» ─────────────────────────────────────────────────────────

function SlicesTab({ facets, params, onShow }) {
  const [by, setBy] = useState('press');
  const [by2, setBy2] = useState('');
  const [min, setMin] = useState(15);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.group({ ...params, by, by2, min })
      .then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [params, by, by2, min]);

  const groups = (facets.groupable || []);
  const g = data && data.group;
  const maxRate = g ? Math.max(...(g.rows || []).map(r => r.rate), 1) : 1;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ ...CARD, display: 'flex', gap: 12, flexWrap: 'wrap',
        alignItems: 'center' }}>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          Разложить по
          <select value={by} onChange={e => setBy(e.target.value)}
            style={{ ...BTN, padding: '6px 8px' }}>
            {groups.map(o => <option key={o.key} value={o.key}>{o.title}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          и ещё по
          <select value={by2} onChange={e => setBy2(e.target.value)}
            style={{ ...BTN, padding: '6px 8px' }}>
            <option value="">— не надо —</option>
            {groups.filter(o => o.key !== by).map(o =>
              <option key={o.key} value={o.key}>{o.title}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 6,
          alignItems: 'center' }}>
          прятать группы меньше
          <input type="number" value={min} min={1}
            onChange={e => setMin(Math.max(1, +e.target.value || 1))}
            style={{ ...BTN, width: 66 }} />
          листов
        </label>
      </div>

      {!data ? <Muted size={14}>Считаю…</Muted> : !g || !g.rows.length ? (
        <div style={{ ...CARD, color: 'var(--muted)' }}>
          По этому признаку в выборке ничего не набралось.
          {g && g.hidden > 0 && ` Скрыто групп меньше порога: ${g.hidden}.`}
        </div>
      ) : (
        <div style={CARD}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
            {g.title}: {n(g.sheets)} листов, {n(g.defects)} не 1-го сорта.
            Средний уровень по этой выборке — {pct(g.overall)}.
            {g.hidden > 0 && ` Скрыто мелких групп: ${g.hidden}.`}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={TH}>{g.title}</th>
                <th style={{ ...TH, textAlign: 'right' }}>Листов</th>
                <th style={{ ...TH, textAlign: 'right' }}>Не 1-й</th>
                <th style={TH}>Доля</th>
                <th style={{ ...TH, textAlign: 'right' }}>К среднему</th>
                <th style={TH}></th>
              </tr></thead>
              <tbody>
                {g.rows.map((r, i) => (
                  <tr key={i} style={{ cursor: r.filter && Object.keys(r.filter).length ? 'pointer' : 'default' }}
                    onClick={() => r.filter && Object.keys(r.filter).length > 0
                      && onShow(r.filter, `${g.title}: ${r.name}`)}>
                    <td style={TD}>
                      {r.name}
                      <div><Muted size={11}>{modeLine(r.mode)}</Muted></div>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>{n(r.sheets)}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{n(r.defects)}</td>
                    <td style={{ ...TD, minWidth: 140 }}>
                      <div style={{ display: 'flex', gap: 8,
                        alignItems: 'center' }}>
                        <Bar value={r.rate} max={maxRate}
                          danger={r.ratio >= 1.5} />
                        <span style={{ width: 48 }}>{pct(r.rate)}</span>
                      </div>
                    </td>
                    <td style={{ ...TD, textAlign: 'right',
                      color: r.ratio >= 1.5 ? 'var(--danger)'
                        : (r.ratio <= 0.5 ? 'var(--success)' : 'var(--muted)') }}>
                      {r.ratio ? `${r.ratio}×` : '—'}
                    </td>
                    <td style={TD}>
                      {/* Условия приходят с сервера вместе со строкой, и
                          собирать их здесь заново нельзя: собранный на экране
                          фильтр разойдётся с тем, по которому посчитана
                          цифра. Пустой — признак фильтром не выражается
                          (рабочий день это период, а не графа), и кнопки
                          тогда нет вовсе. */}
                      {r.filter && Object.keys(r.filter).length > 0 && (
                        <span style={{ color: 'var(--accent)', fontSize: 12 }}>листы →</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.cross && (
        <div style={CARD}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
            {data.cross.title_a} × {data.cross.title_b}
          </div>
          {/* Ради этой таблицы сюда и приходят во второй раз: она отличает
              «хуже везде» от «хуже только в одном сочетании», а это разные
              поломки и разный ремонт. */}
          <Muted>
            В клетке — доля не 1-го сорта. Пусто значит, что такого
            сочетания не было вовсе, а не что всё вышло годным. Нажмите на
            клетку — откроются её листы.
          </Muted>
          <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={TH}></th>
                {data.cross.columns.map(c => <th key={c} style={TH}>{c}</th>)}
              </tr></thead>
              <tbody>
                {data.cross.rows.map(r => (
                  <tr key={r.name}>
                    <td style={{ ...TD, fontWeight: 600 }}>{r.name}</td>
                    {r.cells.map((c, i) => (
                      <td key={i}
                        onClick={() => c.filter && Object.keys(c.filter).length > 0
                          && onShow(c.filter, `${r.name} × ${data.cross.columns[i]}`)}
                        style={{
                        ...TD, textAlign: 'center',
                        cursor: c.filter && Object.keys(c.filter).length ? 'pointer' : 'default',
                        background: c.empty ? 'transparent'
                          : (c.rate >= data.cross.overall * 1.5
                            ? 'rgba(229,97,94,.18)'
                            : (c.rate === 0 ? 'rgba(70,192,138,.12)'
                              : 'transparent')),
                      }}>
                        {c.empty ? <Muted>—</Muted> : (
                          <>
                            {pct(c.rate)}
                            <div><Muted size={10.5}>{n(c.sheets)}</Muted></div>
                          </>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Вкладка «Листы» ─────────────────────────────────────────────────────────

const SOURCE_LABEL = { device: 'станок', operator: 'смена', '': '—' };

// ── Реестр производства ─────────────────────────────────────────────────────
//
// Одна строка — один лист, и в ней ВСЁ, что о нём известно: где сделан, кем,
// под какой заказ, в каком режиме, какой сорт поставили, куда уехал, трогали
// ли его после приёмки.
//
// Колонок сорок с лишним, и показывать их все разом нельзя: сорок колонок —
// это горизонтальная прокрутка вместо таблицы, и первое, что делает человек, —
// закрывает экран. Поэтому видимые колонки выбирает он сам, выбор запоминается,
// а всё невыбранное открывается щелчком по строке. Ничего не спрятано —
// свёрнуто.
//
// Список колонок приходит С СЕРВЕРА, тем же, по которому собирается Excel.
// Объявить его здесь во второй раз значило бы получить две копии, которые
// молча разойдутся: в таблицу добавили «Филиал», в файл забыли, и человек
// выгружает не то, что видит.

const COLS_KEY = 'lamtech_registry_cols';

function loadCols(fallback) {
  try {
    const raw = JSON.parse(localStorage.getItem(COLS_KEY));
    if (Array.isArray(raw) && raw.length) return raw;
  } catch (e) { /* поломанная запись — берём умолчание, а не падаем */ }
  return fallback;
}

// Как показать значение в клетке. Excel рисует то же самое своим `cell()` —
// правила у них общие по смыслу: числа вправо, «да»/пусто вместо «нет».
function Cell({ r, col }) {
  const v = r[col.key];
  if (col.key === 'source') {
    return (
      <>
        <Muted>{SOURCE_LABEL[r.source] || r.source || '—'}</Muted>
        {r.stale && r.stale.length > 0 && (
          <span title={`Датчик молчал: ${r.stale.join(', ')}`}
            style={{ color: 'var(--warning)', marginLeft: 5 }}>!</span>
        )}
      </>
    );
  }
  if (col.kind === 'bool') {
    // Пусто, а не «нет»: колонка из семнадцати тысяч «нет» нечитаема, взгляд
    // должен цепляться за то, что произошло.
    return v ? <span>да</span> : <Muted>·</Muted>;
  }
  if (v === null || v === undefined || v === '') return <Muted>—</Muted>;
  if (col.key === 'grade') {
    return <span style={{ color: r.is_defect ? 'var(--danger)' : 'var(--text)' }}>{v}</span>;
  }
  if (col.key.startsWith('dev_')) return <span>{signed(v, '')}</span>;
  return <span>{v}</span>;
}

function RegistryRow({ r, cols, rest, open, onToggle }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer',
        // Выведенный из счёта лист виден, но приглушён: бирка живая, а в
        // остаток и отчёты он не идёт, и спутать их нельзя.
        opacity: r.voided ? 0.55 : 1 }}>
        {cols.map(c => (
          <td key={c.key} style={{ ...TD,
            textAlign: c.kind === 'num' ? 'right' : 'left',
            fontFamily: c.key === 'code' ? 'monospace' : 'inherit' }}>
            <Cell r={r} col={c} />
          </td>
        ))}
      </tr>
      {open && (
        <tr>
          <td colSpan={cols.length} style={{ ...TD, background: 'var(--input)',
            whiteSpace: 'normal' }}>
            {/* Остальные колонки — те, что человек не вывел в таблицу. Список
                собирается из тех же данных, поэтому «остальное» здесь всегда
                означает ровно «всё, кроме показанного». */}
            <div style={{ display: 'grid', gap: 8,
              gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
              {rest.map(c => (
                <div key={c.key}>
                  <div><Muted size={11}>{c.title.toUpperCase()}</Muted></div>
                  <div style={{ fontSize: 13.5 }}><Cell r={r} col={c} /></div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Выбор колонок. Сгруппирован так же, как объявлен на сервере: сорок галочек
// подряд — это не выбор, а поиск глазами.
function ColumnPicker({ all, value, onChange, onReset }) {
  const [open, setOpen] = useState(false);
  const groups = [];
  all.forEach(c => {
    let g = groups.find(x => x.name === c.group);
    if (!g) { g = { name: c.group, items: [] }; groups.push(g); }
    g.items.push(c);
  });
  const toggle = (key) => {
    // Порядок колонок держим тот же, что на сервере: человек включает
    // «Заказчика» и ждёт его на своём месте, а не в конце таблицы.
    const has = value.includes(key);
    const next = has ? value.filter(k => k !== key)
      : all.filter(c => value.includes(c.key) || c.key === key).map(c => c.key);
    onChange(next);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button style={BTN} onClick={() => setOpen(!open)}>
        Колонки ({value.length}) {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '110%', right: 0,
          width: 380, maxHeight: 440, overflowY: 'auto', ...CARD,
          boxShadow: '0 8px 28px rgba(0,0,0,.35)' }}>
          {groups.map(g => (
            <div key={g.name} style={{ marginBottom: 10 }}>
              <div style={{ marginBottom: 4 }}>
                <Muted size={11}>{g.name.toUpperCase()}</Muted>
              </div>
              <div style={{ display: 'grid', gap: 2,
                gridTemplateColumns: '1fr 1fr' }}>
                {g.items.map(c => (
                  <label key={c.key} style={{ fontSize: 12.5, display: 'flex',
                    gap: 5, alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={value.includes(c.key)}
                      onChange={() => toggle(c.key)} />
                    {c.title}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button style={BTN} onClick={() => setOpen(false)}>Готово</button>
            <button style={BTN} onClick={onReset}>Вернуть обычные</button>
            <button style={BTN}
              onClick={() => onChange(all.map(c => c.key))}>Все</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RegistryTab({ facets, params, onExport }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('-accepted_iso');
  const [open, setOpen] = useState(null);

  const all = useMemo(() => facets.columns || [], [facets.columns]);
  const fallback = useMemo(
    () => facets.default_columns || [], [facets.default_columns]);
  const [picked, setPicked] = useState(null);

  // Пока справочник колонок не пришёл, выбирать не из чего. Как только пришёл
  // — берём запомненный набор, а если его нет, набор по умолчанию.
  useEffect(() => {
    if (!all.length) return;
    setPicked(prev => (prev && prev.length ? prev : loadCols(fallback)));
  }, [all, fallback]);

  const setCols = useCallback((next) => {
    setPicked(next);
    try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); }
    catch (e) { /* приватный режим — выбор просто не запомнится */ }
  }, []);

  useEffect(() => { setPage(1); }, [params]);
  useEffect(() => {
    setData(null);
    api.sheets({ ...params, page, sort })
      .then(r => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [params, page, sort]);

  const cols = useMemo(
    () => all.filter(c => (picked || []).includes(c.key)), [all, picked]);
  const rest = useMemo(
    () => all.filter(c => !(picked || []).includes(c.key)), [all, picked]);

  const click = (key) => {
    setSort(sort === key ? `-${key}` : key);
    setPage(1);
  };

  if (!data || picked === null) return <Muted size={14}>Считаю…</Muted>;
  const s = data.summary || {};
  const st = data.stats || {};

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...CARD, display: 'flex', gap: 16, flexWrap: 'wrap',
        alignItems: 'center', fontSize: 14 }}>
        <span>Листов: <b>{n(s.sheets)}</b></span>
        <span>Не 1-й сорт: <b>{n(s.defects)}</b> ({pct(s.rate)})</span>
        <span><Muted>замерено станком: {n(st.from_device)}</Muted></span>
        {st.dropped_by_mode > 0 && (
          // Молчать об этом нельзя: человек поставил границу по температуре и
          // не понимает, куда делась половина выборки. Она не «пропала» — у
          // тех листов температуры не мерили вовсе.
          <span><Muted>
            не подошли под условия по режиму: {n(st.dropped_by_mode)}
          </Muted></span>
        )}
        {st.truncated && (
          <span style={{ color: 'var(--warning)' }}>
            Листов больше {n(st.limit)} — показано и посчитано не всё,
            сузьте период.
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ColumnPicker all={all} value={picked} onChange={setCols}
            onReset={() => setCols(fallback)} />
          {/* Выгружается ровно то, что на экране: те же колонки, тот же
              порядок, та же сортировка, тот же фильтр. */}
          <button style={BTN_MAIN}
            onClick={() => onExport({ cols: (picked || []).join(','), sort })}>
            Выгрузить в Excel
          </button>
        </div>
      </div>

      {!cols.length ? (
        <div style={{ ...CARD, color: 'var(--muted)' }}>
          Не выбрано ни одной колонки — нажмите «Колонки».
        </div>
      ) : !data.rows.length ? (
        <div style={{ ...CARD, color: 'var(--muted)' }}>
          Под эти условия не подошёл ни один лист.
        </div>
      ) : (
        <div style={{ ...CARD, padding: 0, overflowX: 'auto',
          maxHeight: '72vh', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {cols.map(c => (
                <th key={c.key} style={{ ...TH, cursor: 'pointer',
                  textAlign: c.kind === 'num' ? 'right' : 'left' }}
                  onClick={() => click(c.key)}>
                  {c.title}
                  {sort.replace('-', '') === c.key
                    && (sort.startsWith('-') ? ' ▾' : ' ▴')}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <RegistryRow key={r.id} r={r} cols={cols} rest={rest}
                  open={open === r.id}
                  onToggle={() => setOpen(open === r.id ? null : r.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button style={BTN} disabled={page <= 1}
            onClick={() => setPage(page - 1)}>← назад</button>
          <Muted size={13}>страница {data.page} из {data.pages}</Muted>
          <button style={BTN} disabled={page >= data.pages}
            onClick={() => setPage(page + 1)}>вперёд →</button>
        </div>
      )}

      {(data.averages || []).some(a => a.shift !== null) && (
        <div style={CARD}>
          <h3 style={{ fontSize: 13, color: 'var(--dim)', marginBottom: 10,
            textTransform: 'uppercase' }}>
            Режим: годные листы против брака
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Величина</th>
              <th style={{ ...TH, textAlign: 'right' }}>1-й сорт</th>
              <th style={{ ...TH, textAlign: 'right' }}>Не 1-й</th>
              <th style={{ ...TH, textAlign: 'right' }}>Разница</th>
              <th style={{ ...TH, textAlign: 'right' }}>Разброс у всех</th>
            </tr></thead>
            <tbody>
              {data.averages.filter(a => a.all.n > 0).map(a => (
                <tr key={a.key}>
                  <td style={TD}>{a.title}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    {a.good.avg === null ? <Muted>—</Muted> : a.good.avg}{' '}
                    <Muted size={11}>({n(a.good.n)})</Muted>
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    {a.bad.avg === null ? <Muted>—</Muted> : a.bad.avg}{' '}
                    <Muted size={11}>({n(a.bad.n)})</Muted>
                  </td>
                  <td style={{ ...TD, textAlign: 'right',
                    color: a.shift === null ? 'var(--dim)'
                      : (Math.abs(a.shift) > 0 ? 'var(--warning)' : 'var(--muted)') }}>
                    {signed(a.shift, a.unit)}
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    <Muted>{a.all.min}…{a.all.max}</Muted>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Экран целиком ───────────────────────────────────────────────────────────

const PERIODS = [
  { d: 7, t: 'неделя' }, { d: 30, t: 'месяц' },
  { d: 90, t: '3 месяца' }, { d: 365, t: 'год' },
];

export default function Quality() {
  // Цех начинается с того, что выбран в приложении, и выбор здесь пишется
  // туда же. Иначе перехватчик запросов подставлял бы старый цех поверх
  // выбранного на этой странице, и экран показывал бы одно, а считал другое.
  const [f, setFRaw] = useState(() => {
    let b = '';
    try { b = sessionStorage.getItem('selected_branch') || ''; } catch (e) { /* нет хранилища */ }
    return { ...EMPTY, branch: b === 'all' ? '' : b };
  });
  // Пишем в хранилище внутри самого обновления, а не эффектом: эффекты
  // вкладок срабатывают раньше эффектов страницы, и первый запрос ушёл бы
  // ещё со старым цехом.
  const setF = useCallback((next) => setFRaw(prev => {
    const v = typeof next === 'function' ? next(prev) : next;
    try { sessionStorage.setItem('selected_branch', v.branch || 'all'); } catch (e) { /* нет хранилища */ }
    return v;
  }), []);
  const [tab, setTab] = useState('insights');
  const [facets, setFacets] = useState({});
  const [insights, setInsights] = useState(null);
  const [panel, setPanel] = useState(false);
  const [err, setErr] = useState('');

  // Условия собираются ОДИН раз и уходят во все вкладки одинаковыми. useMemo
  // здесь не про скорость: без него объект пересоздаётся на каждый рендер, и
  // вкладки перезапрашивают данные бесконечно.
  const params = useMemo(() => toParams(f), [f]);
  const paramsKey = JSON.stringify(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useMemo(() => params, [paramsKey]);

  useEffect(() => {
    api.facets({ days: f.days, from: f.from, to: f.to })
      .then(r => setFacets(r.data)).catch(() => setFacets({}));
  }, [f.days, f.from, f.to, f.branch]);

  // Цех, оставшийся в хранилище от другого экрана, этому человеку может быть
  // и не открыт. Такой выбор не показать в списке — значит молча считать по
  // пустой выборке; возвращаем к «все цеха».
  useEffect(() => {
    const list = facets.branch || [];
    if (f.branch && list.length > 1 && !list.some(b => String(b.id) === f.branch)) {
      setF(prev => ({ ...prev, branch: '' }));
    }
  }, [facets.branch, f.branch, setF]);

  useEffect(() => {
    setInsights(null);
    setErr('');
    api.insights(stable)
      .then(r => setInsights(r.data))
      .catch(e => {
        setInsights({ enough: false, sheets: 0, min_sheets: 20 });
        setErr(e?.response?.status === 403
          ? 'Этот разбор открыт технологу по качеству, технологу и директору.'
          : 'Не получилось посчитать. Попробуйте сузить период.');
      });
  }, [stable]);

  // Нажатая строка открывает листы СБОКУ, не уводя с экрана. Условия — ровно
  // те, по которым посчитана цифра, поверх общего фильтра страницы.
  const [drawer, setDrawer] = useState(null);
  const showSheets = useCallback((add, title) => {
    if (!add || !Object.keys(add).length) return;
    setErr('');
    setDrawer({ add, title: title || 'выборка' });
  }, []);
  const closeDrawer = useCallback(() => setDrawer(null), []);
  const drawerParams = useMemo(
    () => (drawer ? { ...stable, ...drawer.add } : null), [drawer, stable]);
  // «Оставить этот фильтр»: условия строки становятся фильтром страницы, и
  // человек продолжает уже внутри них — на той же вкладке.
  const keepDrawer = useCallback(() => {
    if (!drawer) return;
    setF(prev => applyInsightFilter(prev, drawer.add));
    setDrawer(null);
  }, [drawer, setF]);

  // Выгрузка идёт с тем же фильтром, что на экране, плюс то, что видно
  // сейчас: набор колонок и сортировка. Файл обязан совпадать с таблицей —
  // иначе спорить с ним нечем, он выглядит полным.
  const exportWith = useCallback((base) => (extra) => {
    api.download({ ...base, ...(extra || {}) })
      .catch(() => setErr('Выгрузка не удалась.'));
  }, []);
  const onExport = useMemo(() => exportWith(stable), [exportWith, stable]);
  const onDrawerExport = useMemo(
    () => exportWith(drawerParams || stable), [exportWith, drawerParams, stable]);

  const TITLE = { insights: 'Где проблема', slices: 'Сравнить',
    sheets: 'Все листы' };

  return (
    <Layout title={`Качество — ${TITLE[tab]}`}>
    <div style={{ padding: 18, maxWidth: 1400, margin: '0 auto' }}>
      {/* Разделы — кнопками наверху, а не в боковом меню: их три, и все три
          должны быть видны сразу, а не находиться. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            ...BTN, padding: '9px 18px', fontSize: 14,
            fontWeight: tab === t.key ? 700 : 500,
            background: tab === t.key ? 'var(--accent)' : 'var(--input)',
            color: tab === t.key ? '#fff' : 'var(--text)',
            borderColor: tab === t.key ? 'var(--accent)' : 'var(--border)' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
        alignItems: 'flex-start', marginBottom: 12 }}>
        {/* Цех — рядом с периодом, а не в панели фильтра: он меняет смысл
            всех цифр разом, и спрятанный за кнопкой забывается первым. */}
        {(facets.branch || []).length > 1 && (
          <select value={f.branch} onChange={e => setF({ ...f, branch: e.target.value })}
            style={{ ...BTN, fontWeight: 600,
              ...(f.branch ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : {}) }}>
            <option value="">Все цеха</option>
            {facets.branch.map(b => (
              <option key={b.id} value={String(b.id)}>{b.name} · {n(b.sheets)}</option>
            ))}
          </select>
        )}
        {PERIODS.map(p => (
          <button key={p.d} onClick={() => setF({ ...f, days: p.d, from: '', to: '' })}
            style={{ ...BTN,
              background: (!f.from && !f.to && f.days === p.d)
                ? 'var(--accent)' : 'var(--input)',
              color: (!f.from && !f.to && f.days === p.d) ? '#fff' : 'var(--text)',
              borderColor: (!f.from && !f.to && f.days === p.d)
                ? 'var(--accent)' : 'var(--border)' }}>
            {p.t}
          </button>
        ))}
        <input type="date" value={f.from} style={{ ...BTN, width: 145 }}
          onChange={e => setF({ ...f, from: e.target.value })} />
        <input type="date" value={f.to} style={{ ...BTN, width: 145 }}
          onChange={e => setF({ ...f, to: e.target.value })} />
        <button style={{ ...BTN, marginLeft: 'auto' }}
          onClick={() => setPanel(!panel)}>
          {panel ? 'Скрыть фильтры' : 'Все фильтры'}
        </button>
      </div>

      {/* Три признака, по которым ищут чаще всего, — на виду. Остальные
          тридцать — за кнопкой «Все фильтры». */}
      {!panel && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
          alignItems: 'flex-start', marginBottom: 12 }}>
          <Picker title="Пресс" options={facets.press} value={f.press}
            onChange={v => setF({ ...f, press: v })} />
          <Picker title="Оператор" options={facets.operator} value={f.operator}
            onChange={v => setF({ ...f, operator: v })} />
          <Picker title="Смена" options={facets.shift} value={f.shift}
            onChange={v => setF({ ...f, shift: v })} />
          <Picker title="Сорт" options={facets.grade} value={f.grade}
            onChange={v => setF({ ...f, grade: v })} />
        </div>
      )}

      {panel && (
        <FilterPanel facets={facets} f={f} set={setF}
          onClose={() => setPanel(false)} />
      )}
      <ActiveFilters f={f} facets={facets} set={setF} />

      {err && (
        <div style={{ ...CARD, borderLeft: '3px solid var(--warning)',
          marginBottom: 12, fontSize: 13.5 }}>{err}</div>
      )}

      {tab === 'insights' && <InsightsTab data={insights} onShow={showSheets} />}
      {tab === 'slices' && (
        <SlicesTab facets={facets} params={stable} onShow={showSheets} />
      )}
      {tab === 'sheets' && (
        <RegistryTab facets={facets} params={stable} onExport={onExport} />
      )}
    </div>
    {drawer && (
      <SheetsDrawer title={drawer.title} facets={facets} params={drawerParams}
        onExport={onDrawerExport} onOpenFull={keepDrawer} onClose={closeDrawer} />
    )}
    </Layout>
  );
}
